import { 
  Home, 
  Utensils, 
  Scissors, 
  Heart, 
  AlertTriangle,
  CheckCircle
} from "lucide-react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const tips = {
  preparation: {
    icon: Home,
    title: "Préparer l'arrivée",
    items: [
      "Prévoyez une caisse de transport grande taille (le Sibérien est un grand chat)",
      "Installez un arbre à chat solide et assez haut",
      "Choisissez une litière fermée ou à bords hauts",
      "Préparez des gamelles en céramique ou inox (évitez le plastique)",
      "Sécurisez balcons et fenêtres avec des filets",
      "Créez un espace calme pour les premiers jours",
    ],
  },
  alimentation: {
    icon: Utensils,
    title: "Alimentation",
    items: [
      "Croquettes de haute qualité (minimum 40% de protéines animales)",
      "Pâtée en complément pour l'hydratation",
      "Eau fraîche toujours disponible (fontaine à eau recommandée)",
      "Évitez les changements alimentaires brutaux",
      "Friandises naturelles avec modération",
      "Pas de lait de vache (intolérance au lactose)",
    ],
  },
  entretien: {
    icon: Scissors,
    title: "Entretien du pelage",
    items: [
      "Brossage hebdomadaire (2-3 fois par semaine en période de mue)",
      "Utilisez une brosse à poils longs ou un peigne démêloir",
      "Vérifiez régulièrement les oreilles",
      "Coupez les griffes si nécessaire (toutes les 3-4 semaines)",
      "Le bain est rarement nécessaire (pelage autonettoyant)",
      "Inspectez le pelage pour détecter d'éventuels parasites",
    ],
  },
  sante: {
    icon: Heart,
    title: "Santé & bien-être",
    items: [
      "Visite vétérinaire annuelle + rappels de vaccins",
      "Vermifugation 2 à 4 fois par an selon le mode de vie",
      "Anti-puces/tiques si accès à l'extérieur",
      "Surveillez le poids (le Sibérien peut peser 6-9 kg)",
      "Stimulez-le avec des jeux interactifs quotidiens",
      "Respectez son besoin de calme et de sommeil",
    ],
  },
};

const doAndDont = {
  do: [
    "Laissez-lui le temps de s'adapter à son nouvel environnement",
    "Parlez-lui doucement les premiers jours",
    "Établissez une routine (repas, jeux, câlins)",
    "Socialisez-le progressivement avec les autres animaux",
    "Offrez-lui des espaces en hauteur (il adore observer)",
  ],
  dont: [
    "Ne le forcez pas à sortir de sa cachette les premiers jours",
    "Évitez les mouvements brusques et les bruits forts au début",
    "Ne changez pas son alimentation brutalement",
    "N'ignorez pas les signes de stress (miaulements, cachette prolongée)",
    "Ne le laissez pas sortir avant qu'il connaisse bien son territoire",
  ],
};

export default function TipsSection() {
  return (
    <section
      id="conseils"
      data-testid="tips-section"
      className="relative py-24 lg:py-32 bg-cream overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section header */}
        <div className="section-animate text-center mb-12">
          <span className="font-accent text-autumn text-xl">Guide pratique</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-forest mt-2 mb-6">
            Conseils pour Votre Sibérien
          </h2>
          <p className="font-body text-lg text-forest/70 max-w-2xl mx-auto">
            Tout ce que vous devez savoir pour accueillir et prendre soin
            de votre nouveau compagnon dans les meilleures conditions.
          </p>
        </div>

        {/* Tabs */}
        <div className="section-animate">
          <Tabs defaultValue="preparation" className="w-full">
            <TabsList className="flex flex-wrap justify-center gap-2 bg-transparent mb-8">
              {Object.entries(tips).map(([key, value]) => (
                <TabsTrigger
                  key={key}
                  value={key}
                  className="flex items-center gap-2 bg-white border border-sand/50 data-[state=active]:bg-forest data-[state=active]:text-white data-[state=active]:border-forest px-4 py-2 rounded-full font-body font-medium transition-all"
                  data-testid={`tab-${key}`}
                >
                  <value.icon className="w-4 h-4" />
                  {value.title}
                </TabsTrigger>
              ))}
            </TabsList>

            {Object.entries(tips).map(([key, value]) => (
              <TabsContent key={key} value={key}>
                <div className="bg-white rounded-3xl p-8 shadow-soft">
                  <h3 className="font-heading text-2xl font-semibold text-forest mb-6 flex items-center gap-3">
                    <value.icon className="w-6 h-6 text-autumn" />
                    {value.title}
                  </h3>
                  <ul className="grid sm:grid-cols-2 gap-4">
                    {value.items.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-forest flex-shrink-0 mt-0.5" />
                        <span className="font-body text-forest/80">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>

        {/* Do's and Don'ts */}
        <div className="section-animate grid md:grid-cols-2 gap-8 mt-12">
          {/* Do's */}
          <div className="bg-forest/5 border border-forest/10 rounded-2xl p-6">
            <h4 className="font-heading text-xl font-semibold text-forest mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-forest" />
              À faire
            </h4>
            <ul className="space-y-3">
              {doAndDont.do.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-forest font-bold">✓</span>
                  <span className="font-body text-sm text-forest/80">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Don'ts */}
          <div className="bg-autumn/5 border border-autumn/10 rounded-2xl p-6">
            <h4 className="font-heading text-xl font-semibold text-forest mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-autumn" />
              À éviter
            </h4>
            <ul className="space-y-3">
              {doAndDont.dont.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-autumn font-bold">✗</span>
                  <span className="font-body text-sm text-forest/80">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
